'use client'

import { useMemo } from 'react'

import csb from '@/assets/svgs/commanderspellbook.svg'
import { ButtonDefault } from '@/components/common/Button'
import { CardRec } from '@/components/common/Card'
import { ErrorBlock } from '@/components/common/ErrorBlock'
import { SectionLabel } from '@/components/common/SectionLabel'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { usePage } from '@/components/providers/PageProvider'
import { useCardMap } from '@/hooks/useCardMap'
import { cn, isNonEmptyString } from '@/utils/commonUtil'
import { buildRec } from '@/utils/recUtil'

import { FeaturedCommonRoot } from '../FeaturedCommon/FeaturedCommonRoot/FeaturedCommonRoot'

import styles from './FeaturedCombo.module.scss'
import { FeaturedComboBracket } from './FeaturedComboBracket/FeaturedComboBracket'
import { FeaturedComboDetails } from './FeaturedComboDetails/FeaturedComboDetails'
import { FeaturedComboHeading } from './FeaturedComboHeading/FeaturedComboHeading'
import { FeaturedComboMetadata } from './FeaturedComboMetadata/FeaturedComboMetadata'
import { FeaturedComboStats } from './FeaturedComboStats/FeaturedComboStats'
import { FeaturedComboVote } from './FeaturedComboVote/FeaturedComboVote'

export const FeaturedCombo = () => {
  const { page } = usePage()
  const combo = page?.featuredCombo
  const cardIds = useMemo(() => combo?.cards.map(({ cardId }) => cardId), [combo])
  const { cardMap } = useCardMap({ cardIds })

  if (!combo) return <ErrorBlock />

  const title = page.metadata?.title || 'Title'
  const label = page.metadata?.label || 'Combos'

  const backgroundImages = combo.cards
    .slice(0, 3)
    .map(({ cardId }) => cardMap[cardId]?.imageArt)
    .filter(isNonEmptyString)

  const cardButtons = page.gallery?.categories?.slice(0, 4)

  const hasThreeOrMoreCards = combo.cards.length >= 3

  return (
    <FeaturedCommonRoot backgroundImages={backgroundImages} splashProps={{ align: 'right' }}>
      <div className={cn(styles.container, { hasThreeOrMoreCards, styles })}>
        <div className={styles.top}>
          <div className={styles.info}>
            <div className={styles.label}>
              <SectionLabel label={label} />
            </div>
            <h1 className={styles.title}>{title}</h1>
            <div className={styles.stats}>
              <FeaturedComboStats stats={['colorId', 'rank', 'requirements', 'deckTotal', 'inclusion']} />
            </div>
            {cardButtons && (
              <div className={styles.jump}>
                <FeaturedComboHeading heading='Played with this combo' icon='link' />
                <div className={styles.jumpButtons}>
                  {cardButtons.map((cardButton, i) => (
                    <ButtonDefault as='LinkHash' colorScheme='content' hash={cardButton.slug} key={i} variant='outline'>
                      <IconWithLabel iconBefore={cardButton.icon}>{cardButton.label}</IconWithLabel>
                    </ButtonDefault>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className={styles.cards}>
            <ul className={styles.cardsList}>
              {combo.cards.map((card, i) => (
                <li key={i}>
                  <CardRec forceLoad rec={buildRec({ ...card, role: 'combo' })} />
                </li>
              ))}
            </ul>
            <div className={styles.relatedButtons}>
              <ButtonDefault
                as='LinkOutbound'
                colorScheme='commanderspellbook'
                href={`https://commanderspellbook.com/combo/${combo.id}`}
                utm={{ medium: 'combo' }}
                variant='outline'
              >
                <div className={styles.csbButton}>
                  <img alt='' decoding='async' loading='lazy' role='presentation' src={csb.src} />
                  <span>View on Commander Spellbook</span>
                </div>
              </ButtonDefault>
              <ButtonDefault
                as='LinkOutbound'
                colorScheme='warning'
                href={`https://commanderspellbook.com/submit-an-update/?comboId=${combo.id}`}
                utm={{ medium: 'combo' }}
                variant='outline'
              >
                <IconWithLabel iconBefore='circleExclamation'>Report Error</IconWithLabel>
              </ButtonDefault>
            </div>
          </div>
        </div>
        <FeaturedComboDetails />
        <FeaturedComboMetadata />
        <FeaturedComboBracket />
        <FeaturedComboVote />
      </div>
    </FeaturedCommonRoot>
  )
}
